import React, { useCallback, useState } from 'react';

import { LandingPage } from './components/LandingPage';
import { PasswordGate } from './components/PasswordGate';
import { SecViewer } from './components/SecViewer';
import { filingPathToUrl } from './utils/sec-url';

interface ViewerParams {
  url: string | null;
  xbrlTag: string | null;
  periodEndDate: string | null;
  periodStartDate: string | null;
  searchText: string | null;
  instance: number | null;
}

function parseParamsFromUrl(): ViewerParams {
  const searchParams = new URLSearchParams(window.location.search);

  const filingPath = searchParams.get('f');
  const url = filingPath
    ? filingPathToUrl(filingPath)
    : searchParams.get('url');

  const xbrlTag = searchParams.get('tag');
  const periodEndDate = searchParams.get('end');
  const periodStartDate = searchParams.get('start');

  const searchText = searchParams.get('s');
  const rawInstance = searchParams.get('i');
  const instance = rawInstance ? parseInt(rawInstance, 10) || 1 : null;

  return { url, xbrlTag, periodEndDate, periodStartDate, searchText, instance };
}

function AppContent(): React.ReactElement {
  const [params, setParams] = useState<ViewerParams>(parseParamsFromUrl);

  const handleFilingChange = useCallback(
    (
      filingPath: string,
      tag: string | null,
      endDate: string | null,
      startDate: string | null = null
    ): void => {
      setParams({
        url: filingPathToUrl(filingPath),
        xbrlTag: tag,
        periodEndDate: endDate,
        periodStartDate: startDate,
        searchText: null,
        instance: null,
      });
      const searchParams = new URLSearchParams();
      searchParams.set('f', filingPath);
      if (tag) searchParams.set('tag', tag);
      if (endDate) searchParams.set('end', endDate);
      if (startDate) searchParams.set('start', startDate);
      window.history.replaceState(null, '', `?${searchParams.toString()}`);
    },
    []
  );

  const isEmbed = new URLSearchParams(window.location.search).has('embed');

  if (params.url) {
    return (
      <SecViewer
        url={params.url}
        xbrlTag={params.xbrlTag}
        periodEndDate={params.periodEndDate}
        periodStartDate={params.periodStartDate}
        searchText={params.searchText}
        instance={params.instance}
        onFilingChange={handleFilingChange}
        embed={isEmbed}
      />
    );
  }

  return <LandingPage />;
}

function App(): React.ReactElement {
  const searchParams = new URLSearchParams(window.location.search);
  const hasFilingUrl = searchParams.has('f') || searchParams.has('url');

  if (hasFilingUrl) {
    return <AppContent />;
  }

  return (
    <PasswordGate>
      <AppContent />
    </PasswordGate>
  );
}

export default App;
